import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Button from "components/button";
import Icon from "components/icon";
import DateRangePicker from "components/date-range-picker";
import { Columns, TableSortState } from "components/table";
import ActionMenu from "components/shared/action-menu";
import { toast } from "react-toastify";
import { useAppDispatch } from "store";
import { setGlobalComponent } from "store/global-components";
import useHistoryPanel from "hooks/shared/useHistoryPanel";
import { useAppTranslation } from "locale/useAppTranslation";
import { errorHandler } from "_services/errorHandler";
import { editDriverAllowancePageRouteName } from "../edit/edit.page";
import {
  useDriverAllowanceServiceV1ListDriverAllowancesQuery,
  useDriverAllowanceServiceV1ActivateDriverAllowancesMutation,
  useDriverAllowanceServiceV1DeactivateDriverAllowancesMutation,
  type DriverAllowanceV1DriverAllowanceRes,
} from "_services/modules/driver_allowance";

const sortFieldMap: Record<string, string> = {
  vehicleType: "vehicle_type_name",
  vehicleModel: "vehicle_model_name",
  tripType: "trip_type_name",
  distanceType: "distance_type_name",
  driverBasicRate: "driver_basic_rate",
  driverIncentiveRate: "driver_insentif_rate",
  waitingTimeRate: "waiting_time_rate_name",
  status: "status",
  createdAt: "created_at",
  updatedAt: "updated_at",
};

export interface DriverAllowanceRow {
  id: string;
  vehicleType: string;
  vehicleModel: string;
  tripType: string;
  distanceType: string;
  distanceRange: string;
  driverBasicRate: number;
  driverIncentiveRate: number;
  waitingTimeRate: string;
  waitingTimeDuration: string;
  status: "active" | "inactive";
  createdBy: string;
  createdAt: string;
  updatedBy: string;
  updatedAt: string;
}

const useDriverAllowanceList = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { t } = useAppTranslation("driverAllowance");
  const [selectedKeys, setSelectedKeys] = useState<Array<string | number>>([]);
  const [sortState, setSortState] = useState<TableSortState>(null);
  const [filterValues, setFilterValues] = useState<Record<string, string>>({});
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [bulkAction, setBulkAction] = useState<string>("");
  const [showBulkConfirmModal, setShowBulkConfirmModal] = useState(false);
  const [showToggleConfirmModal, setShowToggleConfirmModal] = useState(false);
  const [toggleRow, setToggleRow] = useState<DriverAllowanceRow | null>(null);
  const historyPanel = useHistoryPanel("driver_allowance");

  const { data: driverAllowancesData, isLoading, refetch } = useDriverAllowanceServiceV1ListDriverAllowancesQuery({
    page: currentPage,
    size: rowsPerPage,
    sort: sortState?.columnId ? (sortFieldMap[sortState.columnId] ?? sortState.columnId) : "created_at",
    order: sortState?.direction ?? "desc",
    status: filterValues.status || undefined,
    vehicleTypeName: filterValues.vehicleType || undefined,
    vehicleModelName: filterValues.vehicleModel || undefined,
    tripTypeName: filterValues.tripType || undefined,
    distanceTypeName: filterValues.distanceType || undefined,
    driverBasicRate: filterValues.driverBasicRate ? Number(filterValues.driverBasicRate) : undefined,
    driverInsentifRate: filterValues.driverIncentiveRate ? Number(filterValues.driverIncentiveRate) : undefined,
    waitingTimeRateName: filterValues.waitingTimeRate || undefined,
    createdAtFrom: filterValues.createdAt?.split("|")[0] || undefined,
    createdAtTo: filterValues.createdAt?.split("|")[1] || undefined,
    updatedAtFrom: filterValues.updatedAt?.split("|")[0] || undefined,
    updatedAtTo: filterValues.updatedAt?.split("|")[1] || undefined,
  });
  const [activateDriverAllowances] = useDriverAllowanceServiceV1ActivateDriverAllowancesMutation();
  const [deactivateDriverAllowances] = useDriverAllowanceServiceV1DeactivateDriverAllowancesMutation();

  useEffect(() => {
    dispatch(
      setGlobalComponent({
        title: t("page-title-list"),
        hasBackButton: false,
      })
    );
  }, [dispatch, t]);

  const tableData: DriverAllowanceRow[] = useMemo(() => {
    if (!driverAllowancesData?.driver_allowances) return [];
    return driverAllowancesData.driver_allowances.map((item: DriverAllowanceV1DriverAllowanceRes) => ({
      id: item.id || "",
      vehicleType: item.vehicle_type?.name || "",
      vehicleModel: item.vehicle_model?.name || "",
      tripType: item.trip_type?.name || "",
      distanceType: item.distance_type?.name || "",
      distanceRange: "-",
      driverBasicRate: item.driver_basic_rate || 0,
      driverIncentiveRate: item.driver_insentif_rate || 0,
      waitingTimeRate: item.waiting_time_rate?.name || "",
      waitingTimeDuration: "-",
      status: (item.status || "inactive") as "active" | "inactive",
      createdBy: item.created_by || "",
      createdAt: item.created_at || "",
      updatedBy: item.updated_by || "",
      updatedAt: item.updated_at || "",
    }));
  }, [driverAllowancesData]);

  const selectedRows = tableData.filter((row) => selectedKeys.includes(row.id));
  const hasMixedStatus =
    selectedRows.length > 0 &&
    new Set(selectedRows.map((r) => r.status)).size > 1;

  const isBulkActionDisabled =
    selectedKeys.length === 0 || hasMixedStatus;

  const handleApplyBulkAction = () => {
    if (!bulkAction || isBulkActionDisabled) return;
    setShowBulkConfirmModal(true);
  };

  const handleConfirmBulkAction = async () => {
    try {
      const ids = selectedKeys.map((key) => String(key));
      if (bulkAction === "activate") {
        await activateDriverAllowances({ driverAllowanceV1DriverAllowanceBulkStatusChangeReq: { ids } }).unwrap();
        toast.success(t("toast-activate-success"));
      } else if (bulkAction === "deactivate") {
        await deactivateDriverAllowances({ driverAllowanceV1DriverAllowanceBulkStatusChangeReq: { ids } }).unwrap();
        toast.success(t("toast-deactivate-success"));
      }
      setShowBulkConfirmModal(false);
      setBulkAction("");
      setSelectedKeys([]);
      refetch();
    } catch (error) {
      console.error("Bulk action failed:", error);
      errorHandler(error);
    }
  };

  const handleToggleStatus = (row: DriverAllowanceRow) => {
    setToggleRow(row);
    setShowToggleConfirmModal(true);
  };

  const handleConfirmToggle = async () => {
    if (!toggleRow) return;
    try {
      const ids = [toggleRow.id];
      if (toggleRow.status === "active") {
        await deactivateDriverAllowances({ driverAllowanceV1DriverAllowanceBulkStatusChangeReq: { ids } }).unwrap();
        toast.success(t("toast-deactivate-success"));
      } else {
        await activateDriverAllowances({ driverAllowanceV1DriverAllowanceBulkStatusChangeReq: { ids } }).unwrap();
        toast.success(t("toast-activate-success"));
      }
      setShowToggleConfirmModal(false);
      setToggleRow(null);
      refetch();
    } catch (error) {
      console.error("Toggle failed:", error);
      errorHandler(error);
    }
  };

  const handleFilterChange = (values: Record<string, string>) => {
    setFilterValues(values);
    setCurrentPage(1);
  };

  const handleEdit = (row: DriverAllowanceRow) => {
    navigate(editDriverAllowancePageRouteName(row.id.toString()));
  };

  const bulkConfirmMessage = useMemo(() => {
    if (selectedRows.length === 0) return "";
    const status = selectedRows[0]?.status;
    return status === "active"
      ? t("bulk-confirm-deactivate")
      : t("bulk-confirm-activate");
  }, [selectedRows, t]);

  const columns: Columns<DriverAllowanceRow>[] = useMemo(
    () => [
      {
        id: "no",
        fieldId: "index",
        label: t("col-no"),
        width: "60px",
      },
      {
        id: "vehicleType",
        fieldId: "vehicleType",
        label: t("label-vehicle-type"),
        sortable: true,
        filterable: true,
        filterPlaceholder: t("col-search-placeholder"),
      },
      {
        id: "vehicleModel",
        fieldId: "vehicleModel",
        label: t("label-vehicle-model"),
        sortable: true,
        filterable: true,
        filterPlaceholder: t("col-search-placeholder"),
      },
      {
        id: "tripType",
        fieldId: "tripType",
        label: t("label-trip-type"),
        sortable: true,
        filterable: true,
        filterPlaceholder: t("col-search-placeholder"),
      },
      {
        id: "distanceType",
        fieldId: "distanceType",
        label: t("label-distance-type"),
        sortable: true,
        filterable: true,
        filterPlaceholder: t("col-search-placeholder"),
        render: (row) => (
          <div className="text-sm" data-testid={`col-distance-type-${row.id}`}>
            <div>{row.distanceType}</div>
            <div className="text-greyScale-40 text-xs">{row.distanceRange}</div>
          </div>
        ),
      },
      {
        id: "driverBasicRate",
        fieldId: "driverBasicRate",
        label: t("label-driver-basic-rate"),
        sortable: true,
        filterable: true,
        filterPlaceholder: t("col-search-placeholder"),
        render: (row) => (
          <span className="text-sm" data-testid={`col-driver-basic-rate-${row.id}`}>Rp {row.driverBasicRate.toLocaleString('id-ID')}</span>
        ),
      },
      {
        id: "driverIncentiveRate",
        fieldId: "driverIncentiveRate",
        label: t("label-driver-incentive-rate"),
        sortable: true,
        filterable: true,
        filterPlaceholder: t("col-search-placeholder"),
        render: (row) => (
          <span className="text-sm" data-testid={`col-driver-incentive-rate-${row.id}`}>Rp {row.driverIncentiveRate.toLocaleString('id-ID')}</span>
        ),
      },
      {
        id: "waitingTimeRate",
        fieldId: "waitingTimeRate",
        label: t("label-waiting-time-rate"),
        sortable: true,
        filterable: true,
        filterPlaceholder: t("col-search-placeholder"),
        render: (row) => (
          <div className="text-sm" data-testid={`col-waiting-time-rate-${row.id}`}>
            <div>{row.waitingTimeRate}</div>
            <div className="text-greyScale-40 text-xs">{row.waitingTimeDuration}</div>
          </div>
        ),
      },
      {
        id: "status",
        fieldId: "status",
        label: t("label-status"),
        sortable: true,
        filterable: true,
        filterOptions: [
          { label: t("col-all-data"), value: "" },
          { label: t("status-active"), value: "active" },
          { label: t("status-inactive"), value: "inactive" },
        ],
        render: (row) => (
          <div className="inline-flex items-center gap-2 text-sm" data-testid={`col-status-${row.id}`}>
            <span
              className={`h-3 w-3 rounded-full ${
                row.status === "active"
                  ? "bg-primary-600 border-2 border-primary-100"
                  : "bg-greyScale-60 border-2 border-greyScale-90"
              }`}
            />
            <span className="capitalize">{row.status}</span>
          </div>
        ),
      },
      {
        id: "createdAt",
        fieldId: "createdBy",
        fieldId2: "createdAt",
        label: t("col-created-by"),
        sortable: true,
        filterable: true,
        renderFilter: ({ value, onChange }) => {
          const [from = "", to = ""] = value.split("|");
          return (
            <DateRangePicker
              fromValue={from}
              toValue={to}
              onChange={(f, t) => onChange(`${f}|${t}`)}
              reserveHelperSpace={false}
            />
          );
        },
      },
      {
        id: "updatedAt",
        fieldId: "updatedBy",
        fieldId2: "updatedAt",
        label: t("col-updated-by"),
        sortable: true,
        filterable: true,
        renderFilter: ({ value, onChange }) => {
          const [from = "", to = ""] = value.split("|");
          return (
            <DateRangePicker
              fromValue={from}
              toValue={to}
              onChange={(f, t) => onChange(`${f}|${t}`)}
              reserveHelperSpace={false}
            />
          );
        },
      },
      {
        id: "action",
        fieldId: "id",
        label: t("col-action"),
        width: "140px",
        dragDisabled: true,
        render: (row) => (
          <div
            className="flex items-center gap-2"
            onClick={(e) => e.stopPropagation()}
          >
            <Button
              type="button"
              variant="outline"
              color="primary"
              size="small"
              iconLeft={<Icon name="edit-2" size={14} />}
              onClick={() => handleEdit(row)}
              data-testid="btn-edit-allowance"
            >
              {t("btn-edit")}
            </Button>
            <ActionMenu
              items={[
                {
                  label: row.status === "active" ? t("btn-deactivate") : t("btn-activate"),
                  onClick: () => handleToggleStatus(row),
                  variant: row.status === "active" ? "danger" : "default",
                  testId: row.status === "active" ? "btn-deactivate-allowance" : "btn-activate-allowance",
                },
                {
                  label: t("btn-track-history"),
                  onClick: () => historyPanel.openPanel(row.id, row.vehicleType),
                  testId: "btn-track-history-allowance",
                },
              ]}
            />
          </div>
        ),
      },
    ],
    [t],
  );

  const totalPages = driverAllowancesData?.metadata?.total_page ?? 1;

  return {
    tableData,
    isLoading,
    data: tableData,
    columns,
    sortState,
    setSortState,
    filterValues,
    setFilterValues,
    handleFilterChange,
    selectedKeys,
    setSelectedKeys,
    currentPage,
    setCurrentPage,
    rowsPerPage,
    setRowsPerPage,
    totalPages,
    navigate,
    bulkAction,
    setBulkAction,
    isBulkActionDisabled,
    showBulkConfirmModal,
    setShowBulkConfirmModal,
    handleApplyBulkAction,
    handleConfirmBulkAction,
    showToggleConfirmModal,
    setShowToggleConfirmModal,
    toggleRow,
    handleConfirmToggle,
    bulkConfirmMessage,
    historyPanel,
    t,
  };
};

export default useDriverAllowanceList;
